import {
  HOST_URL,
  LOCAL_STORAGE_TOKEN_KEY,
  SOCKET_URL,
  LOCAL_STORAGE_ROBOT,
  LOCAL_STORAGE_LIVE_REPORT,
  getFormattedDate,
} from "../consts";
import axios from "axios";
import { getDateStr } from "./utils";
import { editError } from "./editError/actions/editErrorAction";

export default class ApiHandler {
  constructor(props) {
    this.authToken = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
  }

  get = async (url, key = "result") => {
    const response = await axios.get(url, {
      headers: { Authorization: this.authToken },
    });

    if (key) return response.data[key];
    else return response.data;
  };

  getFile = async (url) => {
    return await axios.get(url, {
      headers: { Authorization: this.authToken },
      responseType: "blob",
    });
  };

  delete = async (url) => {
    const response = await axios.delete(url, {
      headers: { Authorization: this.authToken },
    });
    return response.data.result;
  };

  post = async (url, data, headers = {}, key = "result") => {
    const response = await axios.post(url, data, {
      headers: { ...headers, Authorization: this.authToken },
    });
    if (key) return response.data[key];
    else return response.data;
  };

  patch = async (url, data, headers = {}) => {
    const response = await axios.patch(url, data, {
      headers: { ...headers, Authorization: this.authToken },
    });
    return response.data;
  };

  put = async (url, data, headers = {}, key = "result") => {
    const response = await axios.put(url, data, {
      headers: { ...headers, Authorization: this.authToken },
    });
    return response.data[key];
  };

  getDashboard = async (projectId) =>
    this.get(`${HOST_URL}/api/vehicles/dashboard?projectId=${projectId}`);

  sendCommand = async ({ projectId, command, vehicleId, robotNumber }) => {
    const body = {
      projectId,
      command,
      vehicleId,
      robotNumber,
    };

    const url = `${HOST_URL}/api/vehicles/command`;
    return this.post(url, body);
  };

  sendProjectCommand = async ({
    projectId,
    event,
    category,
    value,
    cmd,
    rpi,
  }) => {
    let url = `${HOST_URL}/api/commands/projects/${projectId}`;

    const body = {
      event,
      category,
      value,
      cmd,
      rpi,
    };

    return this.post(url, body);
  };

  updateVehicleSettings = async (vehicleId, projectId, settings) => {
    const body = {
      projectId,
      vehicleId,
      settings,
    };
    const url = `${HOST_URL}/api/vehicles/settings`;
    return this.post(url, body);
  };
  getRepeaters = async ({ projectId, page = 0, timeZone, date, search }) => {
    return this.get(
      `${HOST_URL}/api/repeaters?projectId=${projectId}&page=${page}&timeZone=${timeZone}&date=${getDateStr(
        date
      )}&search=${search}`
    );
  };

  deleteRepeater = async (repeaterId) => {
    const url = `${HOST_URL}/api/repeaters/${repeaterId}`;
    return this.delete(url);
  };

  getSubnetKits = async (projectId) => {
    let url = `${HOST_URL}/api/projects/${projectId}/subnet-kits`;
    return this.get(url, "subnetKits");
  };

  getProjectSettings = async (projectId) => {
    const url = `${HOST_URL}/api/settings/${projectId}`;
    const response = await axios.get(url, {
      headers: { Authorization: this.authToken },
    });
    return response.data.project;
  };

  getProjectAttachments = async (projectId) => {
    return this.get(`${HOST_URL}/api/attachments?projectId=${projectId}`);
  };

  deleteAttachments = async (projectId, attachments) => {
    for (let i = 0; i < attachments.length; i++) {
      const url = `${HOST_URL}/api/attachments/${attachments[i]._id}`;
      await this.delete(url);
    }
  };

  uploadAttachment = ({ file, projectId, errorId, taskId }) => {
    if (!file) return;

    let url = HOST_URL + "/api/attachments";

    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", projectId);

    if (errorId) formData.append("errorId", errorId);
    if (taskId) formData.append("taskId", taskId);

    return this.post(url, formData, { "content-type": "multipart/form-data" });
  };

  editProject = async (
    projectId,
    params,
    uploadFiles = [],
    deletedAttachments = []
  ) => {
    let url = HOST_URL + "/api/projects/" + projectId;

    const response = await this.patch(url, params);

    if (uploadFiles.length && uploadFiles[0]) {
      await this.uploadAttachment({
        file: uploadFiles[0],
        projectId: response.data.project._id,
      });
    }

    if (deletedAttachments.length)
      await this.deleteAttachments(
        response.data.project._id,
        deletedAttachments,
        0
      );

    return response;
  };

  setRobotSettings = async (robotId, data) => {
    let url = HOST_URL + `/api/robots/${robotId}/settings`;
    const response = await this.patch(url, data);
    return response.robot;
  };

  getErrors = async ({
    projectId,
    search,
    robotId,
    vehicleId,
    from,
    to,
    errorCode,
    sort,
    page,
  }) => {
    if (!from) {
      from = new Date();
      from.setDate(from.getDate() - 7);
    }

    if (!to) to = new Date();

    let url = `${HOST_URL}/api/errors?projectId=${projectId}&from=${from.getTime()}&to=${to.getTime()}`;
    if (search) url += "&search=" + search;
    if (vehicleId) url += "&vehicleId=" + vehicleId;
    if (robotId) url += "&robotId=" + robotId;
    if (errorCode) url += "&errorCode=" + errorCode;
    if (sort) url += "&sort=" + sort;
    if (page) url += "&page=" + page;

    const response = await this.get(url, null);

    return response;
  };

  getAvailabilityXlsx = async (projectId, from, to) => {
    if (!from) {
      from = new Date();
      from.setDate(from.getDate() - 7);
    }

    if (!to) to = new Date();

    let url = `${HOST_URL}/api/availability/export?projectId=${projectId}&from=${from.getTime()}&to=${to.getTime()}`;

    const response = await this.getFile(url);
    return response;
  };

  getErrorsXlsx = async ({
    projectId,
    search,
    robotId,
    vehicleId,
    from,
    to,
    errorCode,
    sort,
  }) => {
    if (!from) {
      from = new Date();
      from.setDate(from.getDate() - 7);
    }

    if (!to) to = new Date();

    let url = `${HOST_URL}/api/errors/export?projectId=${projectId}&from=${from.getTime()}&to=${to.getTime()}`;
    if (search) url += "&search=" + search;
    if (vehicleId) url += "&vehicleId=" + vehicleId;
    if (robotId) url += "&robotId=" + robotId;
    if (errorCode) url += "&errorCode=" + errorCode;
    if (sort) url += "&sort=" + sort;

    const response = await this.getFile(url);
    return response;
  };

  deleteError = async (ids) => {
    let url = HOST_URL + "/api/errors/" + ids.join(",");
    const response = await this.delete(url);
    return ids;
  };

  getUsers = async ({ search = "", projectId = "", role = "" } = {}) => {
    let url = `${HOST_URL}/api/users?search=${search}&projectId=${projectId}&role=${role}`;
    return this.get(url, "users");
  };

  deleteUser = async (userId) => {
    let url = HOST_URL + "/api/users/" + userId;
    return this.delete(url);
  };

  getMinMax = async () => {
    let url = `${HOST_URL}/api/projects/minMax`;
    return this.get(url, "minMax");
  };

  getProjects = async (search = "", includeArchived = false) => {
    console.log(new Error());
    let url = `${HOST_URL}/api/projects/?search=${search}&includeArchived=${includeArchived}`;
    return this.get(url, null);
  };

  getRobotDailyReports = async ({ projectId, date, reportType = "" }) => {
    date = getFormattedDate({ date, dateWithYearDashes: true });
    let url = `${HOST_URL}/api/robot-daily-reports?projectId=${projectId}&date=${date}&reportType=${reportType}`;

    return this.get(url, "robotDailyReports");
  };

  getRobotDailyReportXlsx = async ({ projectId, date, reportType }) => {
    date = getFormattedDate({ date, dateWithYearDashes: true });
    let url = `${HOST_URL}/api/robot-daily-reports/export?projectId=${projectId}&date=${date}&reportType=${reportType}`;

    const response = await this.getFile(url);
    return response;
  };

  getRobotDailyReportSummary = async ({ projectId, date, reportType }) => {
    date = getFormattedDate({ date, dateWithYearDashes: true });
    let url = `${HOST_URL}/api/robot-daily-reports/summary?projectId=${projectId}&date=${date}&reportTypeFilter=${reportType}`;
    return this.get(url, "reports");
  };

  getRobotNumbers = async (projectId) => {
    let url = `${HOST_URL}/api/robots/numbers?projectId=${projectId}`;
    return this.get(url, "robotNumbers");
  };

  createErrorResolution = async (params) => {
    let url = `${HOST_URL}/api/error-resolutions`;
    return this.post(url, params, null, "errorResolution");
  };

  getErrorResolutions = async ({
    projectId,
    robotNumber,
    technicianId,
    status,
    page = 0,
  }) => {
    let url = `${HOST_URL}/api/error-resolutions?projectId=${projectId}&page=${page}`;
    if (robotNumber) url += "&robotNumber=" + robotNumber;
    if (technicianId) url += "&technicianId=" + technicianId;
    if (status) url += "&status=" + status;

    return this.get(url);
  };

  editErrorResolution = async (errorResolutionId, params) => {
    let url = `${HOST_URL}/api/error-resolutions/${errorResolutionId}`;
    return this.patch(url, params);
  };

  createAttachment = async ({
    projectId,
    file,
    type,
    errorId,
    taskId,
    errorResolutionId,
  }) => {
    let url = HOST_URL + "/api/attachments/";

    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", projectId);

    if (errorId) formData.append("errorId", errorId);
    if (errorResolutionId)
      formData.append("errorResolutionId", errorResolutionId);
    if (taskId) formData.append("taskId", taskId);
    if (type) formData.append("type", type);

    const result = await this.post(
      url,
      formData,
      { "content-type": "multipart/form-data" },
      "attachment"
    );
    return result;
  };

  deleteAttachment = async (errorId) => {
    let url = HOST_URL + "/api/attachments/" + errorId;
    return this.delete(url);
  };

  updateAttachment = async (attachmentId, params) => {
    let url = HOST_URL + "/api/attachments/" + attachmentId;
    return this.patch(url, params);
  };

  disconnectRpi = async (projectId) => {
    let url = `${SOCKET_URL}/api/socket/project/disconnect`;
    return this.post(url, { projectId });
  };

  createGroup = async ({ projectGroupId, file, name }) => {
    let url = HOST_URL + "/api/projects/project-groups";

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    if (projectGroupId) formData.append("projectGroupId", projectGroupId);

    const result = await this.post(
      url,
      formData,
      { "content-type": "multipart/form-data" },
      "projectGroup"
    );
    return result;
  };

  deleteProjectGroup = async (projectGroupId) => {
    let url = HOST_URL + "/api/projects/project-groups/" + projectGroupId;
    return this.delete(url);
  };

  // Robot Settings API methods
  getRobotSettings = async (projectId, search = "", page = 0, limit = 100) => {
    let url = `${HOST_URL}/api/robots/settings?projectId=${projectId}`;
    if (search) url += `&search=${search}`;
    if (page) url += `&page=${page}`;
    if (limit) url += `&limit=${limit}`;
    return this.get(url, "data");
  };

  getRobotMoreSettings = async (
    projectId,
    search = "",
    page = 0,
    limit = 100
  ) => {
    let url = `${HOST_URL}/api/robots/more/settings?projectId=${projectId}`;
    if (search) url += `&search=${search}`;
    if (page) url += `&page=${page}`;
    if (limit) url += `&limit=${limit}`;
    return this.get(url, "data");
  };

  exportRobotSettings = async (projectId) => {
    let url = `${HOST_URL}/api/robots/settings/export?projectId=${projectId}`;
    return this.getFile(url);
  };

  exportRobotMoreSettings = async (projectId) => {
    let url = `${HOST_URL}/api/robots/more/settings/export?projectId=${projectId}`;
    return this.getFile(url);
  };

  importRobotSettings = async (formData) => {
    let url = `${HOST_URL}/api/robots/settings/import`;
    return this.post(url, formData, { "content-type": "multipart/form-data" });
  };

  importRobotMoreSettings = async (formData) => {
    let url = `${HOST_URL}/api/robots/more/settings/import`;
    return this.post(url, formData, { "content-type": "multipart/form-data" });
  };

  // Availability API methods
  recalculateAvailability = async (projectId, from, to) => {
    let url = `${HOST_URL}/api/availability/recalculate`;
    return this.post(url, { projectId, from: from.getTime(), to: to.getTime() });
  };

  editAvailability = async (params, availabilityId) => {
    let url = `${HOST_URL}/api/availability/${availabilityId}`;
    return this.patch(url, params);
  };

  // Robot-specific API methods
  getRobotDetails = async (robotId) => {
    let url = `${HOST_URL}/api/robots/${robotId}`;
    const response = await this.get(url, null);
    if (response.success) {
      // Store robot data in localStorage
      const robot = response.data;
      robot.robotId = robot._id;
      localStorage.setItem(LOCAL_STORAGE_ROBOT, JSON.stringify(robot));
      return robot;
    } else {
      throw new Error(response.message || "Failed to get robot details");
    }
  };

  refreshRobotBatteries = async (projectId, robotId, vehicleId) => {
    let category = "project";
    if (robotId) category = "robot";
    else if (vehicleId) category = "vehicle";

    let url = `${HOST_URL}/api/projects/refresh/robotreports?category=${category}&projectId=${projectId}`;
    if (robotId) url += "&value=" + robotId;
    else if (vehicleId) url += "&value=" + vehicleId;

    const response = await this.get(url, null);
    if (response.success) {
      return response.data.processId;
    } else {
      throw new Error(response.message || "Failed to refresh robot batteries");
    }
  };

  getLiveReports = async (projectId, processId, saveData = true) => {
    if (saveData) {
      const local = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_LIVE_REPORT) || "{}"
      );
      local[projectId] = {
        processId,
        time: new Date(),
      };
      localStorage.setItem(LOCAL_STORAGE_LIVE_REPORT, JSON.stringify(local));
    }

    let url = `${HOST_URL}/api/projects/${projectId}/getlivereports?processId=${processId}`;
    const response = await this.get(url, null);
    if (response.success) {
      return response.reports;
    } else {
      throw new Error(response.message || "Failed to get live reports");
    }
  };

  getDailyActivity = async (projectId, startDate, endDate, page) => {
    let url = `${HOST_URL}/api/projects/${projectId}/daily-activity?startDate=${startDate.getTime()}&endDate=${
      endDate ? endDate.getTime() : ""
    }&page=${page}`;

    return this.get(url, "dailyActivity");
  };

  getDailyActivityXlsx = async (projectId, startDate, endDate) => {
    let url = `${HOST_URL}/api/projects/${projectId}/daily-activity/download?startDate=${startDate.getTime()}&endDate=${
      endDate ? endDate.getTime() : ""
    }`;

    const response = await this.getFile(url);
    return response;
  };

  getCleaningData = async (projectId, startDate, endDate, page) => {
    let url = `${HOST_URL}/api/projects/${projectId}/cleaningdata?startDate=${startDate.getTime()}&endDate=${
      endDate ? endDate.getTime() : ""
    }&page=${page}`;

    return this.get(url, "cleaningData");
  };
  // Robot Whitelist methods
  getRobotWhitelist = async (userId, projectId) => {
    let url = `${HOST_URL}/api/robot-whitelist/user/${userId}/project/${projectId}`;
    return this.get(url, "robotWhitelist");
  };

  setRobotWhitelist = async (userId, projectId, whitelist) => {
    let url = `${HOST_URL}/api/robot-whitelist/user/${userId}/project/${projectId}`;
    return this.put(url, { whitelist }, "robotWhitelist");
  };

  getUserRobotWhitelists = async (userId) => {
    let url = `${HOST_URL}/api/robot-whitelist/user/${userId}`;
    return this.get(url, "userRobotWhitelists");
  };

  // User management methods
  editUser = async (userId, params) => {
    let url = `${HOST_URL}/api/users/${userId}`;
    const response = await this.patch(url, params);
    return response.user;
  };

  addUser = async (params) => {
    let url = `${HOST_URL}/api/users/`;

    const response = await this.post(url, params, {}, null);

    if (response.success) {
      return response.user;
    } else {
      throw new Error(response.message);
    }
  };

  // getProjects = async (search = "") => {
  //   let url = `${HOST_URL}/api/projects/`;
  //   if (search) url += "?search=" + search;
  //   const response = await this.get(url, null);
  //   if (response.success) {
  //     return {
  //       projectGroups: response.projectGroups,
  //       searchResults: response.searchResults,
  //     };
  //   } else {
  //     throw new Error(response.message);
  //   }
  // };

  // Message management API methods
  getMessages = async (
    projectId,
    search = "",
    expiryDate = null,
    startDate = null,
    endDate = null
  ) => {
    let url = `${HOST_URL}/api/messages?projectId=${projectId}`;
    if (search) url += "&search=" + search;
    if (expiryDate) url += "&expiryDate=" + expiryDate;
    if (startDate) url += "&startDate=" + startDate.getTime();
    if (endDate) url += "&endDate=" + endDate.getTime();

    const response = await this.get(url, null);
    if (response.success) {
      return {
        messages: response.messages || [],
        totalReadMessages: response.totalReadMessages || 0,
        totalYellowMessages: response.totalYellowMessages || 0,
      };
    } else {
      throw new Error(response.message);
    }
  };

  deleteMessage = async (messageId, projectId) => {
    let url = `${HOST_URL}/api/messages/${messageId}`;
    const response = await axios.delete(url, {
      headers: { Authorization: this.authToken },
      data: { projectId: projectId },
    });

    if (response.data.success) {
      return response.data.message;
    } else {
      throw new Error(response.data.message);
    }
  };

  // Tasks API methods
  getTasks = async (
    projectId,
    search = "",
    startDate = null,
    endDate = null
  ) => {
    let url = `${HOST_URL}/api/tasks?projectId=${projectId}`;
    if (search) url += `&search=${search}`;
    if (startDate) url += `&startDate=${startDate.getTime()}`;
    if (endDate) url += `&endDate=${endDate.getTime()}`;

    const response = await this.get(url, null);
    if (response.success) {
      return {
        tasks: response.tasks || [],
        totalRedTasks: response.totalRedTasks || 0,
        totalYellowTasks: response.totalYellowTasks || 0,
      };
    } else {
      throw new Error(response.message || "Failed to get tasks");
    }
  };

  deleteTask = async (taskId) => {
    let url = `${HOST_URL}/api/tasks/${taskId}`;
    await this.delete(url);
  };

  createTask = async (params) => {
    let url = `${HOST_URL}/api/tasks/`;
    const response = await this.post(url, params, {}, null);
    if (response.success) {
      return response.task;
    } else {
      throw new Error(response.message || "Failed to create task");
    }
  };

  editTask = async (params, taskId) => {
    let url = `${HOST_URL}/api/tasks/${taskId}`;
    const response = await this.patch(url, params);
    if (response.success) {
      return response.task;
    } else {
      throw new Error(response.message || "Failed to edit task");
    }
  };
}
